import { useEffect, useRef } from 'react';
import { AUTOCOMPLETE_FIELDS } from './constants';

interface UsePlacesAutocompleteOptions {
  inputRef: React.RefObject<HTMLInputElement | null>;
  isLoaded: boolean;
  readOnly: boolean;
  showSearch: boolean;
  locationName: string;
  onPlaceChanged: (autocomplete: google.maps.places.Autocomplete) => void;
}

export function usePlacesAutocomplete({
  inputRef,
  isLoaded,
  readOnly,
  showSearch,
  locationName,
  onPlaceChanged,
}: UsePlacesAutocompleteOptions) {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onPlaceChangedRef = useRef(onPlaceChanged);
  onPlaceChangedRef.current = onPlaceChanged;

  useEffect(() => {
    if (
      showSearch &&
      inputRef.current &&
      inputRef.current.value !== locationName
    ) {
      inputRef.current.value = locationName;
    }
  }, [locationName, showSearch, inputRef]);

  useEffect(() => {
    if (
      !isLoaded ||
      readOnly ||
      !showSearch ||
      !inputRef.current ||
      !google?.maps?.places
    ) {
      return undefined;
    }

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      fields: [...AUTOCOMPLETE_FIELDS],
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      onPlaceChangedRef.current(autocomplete);
    });

    return () => {
      listener?.remove();
      google.maps.event.clearInstanceListeners(autocomplete);
      autocompleteRef.current = null;
    };
  }, [inputRef, isLoaded, readOnly, showSearch]);
}
